import React, { useState } from 'react';
import { ArrowRight, Check, Lock, Share2, Users } from 'lucide-react';
import Modal from '../modal/Modal';

export type MembershipConversionChoice = 'group' | 'share';

export type MembershipConversionOptionState = {
    /** 지금 고를 수 있는지. false면 카드는 남기고 이유를 보여준다. */
    isAvailable: boolean;
    /** 고를 수 없는 이유. 카드를 감추는 대신 이 문장으로 다음 행동을 알린다. */
    unavailableReason?: string;
};

type Props = {
    /** PT 수강권만 그룹 전환 대상이다. 그룹 원본에서는 공유 카드만 고를 수 있다. */
    group: MembershipConversionOptionState;
    share: MembershipConversionOptionState;
    onClose: () => void;
    onSelect: (choice: MembershipConversionChoice) => void;
};

type OptionCopy = {
    key: MembershipConversionChoice;
    title: string;
    /** 카드 한 장을 한 문장으로 요약. 가장 크게 읽혀야 하는 줄이다. */
    headline: string;
    icon: React.ReactNode;
    accent: string;
    accentSoft: string;
    facts: Array<{ label: string; value: string; isWarning?: boolean }>;
};

/**
 * 두 전환은 이름만으로는 구분되지 않는다("그룹 전환" vs "공유 전환" 모두 여러 명이
 * 쓰는 것처럼 들린다). 그래서 버튼을 하나로 합치고, 같은 축으로 나란히 놓아
 * 차이를 눈으로 읽게 한다.
 *
 * 위계: 아이콘 + 제목 → 한 줄 요약(가장 크게) → 비교표 → 되돌리기 경고.
 * 비교표를 카드의 주인공으로 두면 어느 것을 고를지가 아니라 표를 읽는 화면이 된다.
 */
const OPTIONS: OptionCopy[] = [
    {
        key: 'group',
        title: '그룹 수업으로 전환',
        headline: '남은 회차를 그룹수업 상품으로 옮깁니다',
        icon: <Users size={20} />,
        accent: '#2563EB',
        accentSoft: '#EFF6FF',
        facts: [
            { label: '쓰는 사람', value: '이 회원 한 명 (그대로)' },
            { label: '원본 수강권', value: '만료 처리 · 그룹 수강권 새로 생성' },
            { label: '매출', value: '중복 반영 없음' },
        ],
    },
    {
        key: 'share',
        title: '회차 공유 시작',
        headline: '이 회차를 다른 회원과 나눠 씁니다',
        icon: <Share2 size={20} />,
        accent: '#008977',
        accentSoft: '#E8F8F5',
        facts: [
            { label: '쓰는 사람', value: '소유자 + 함께 쓸 회원' },
            { label: '원본 수강권', value: '그대로 유지' },
            { label: '매출', value: '변동 없음' },
        ],
    },
];

const REVERSIBILITY: Record<MembershipConversionChoice, { text: string; isWarning: boolean }> = {
    group: { text: '되돌릴 수 없습니다', isWarning: true },
    share: { text: '공유 종료로 언제든 되돌립니다', isWarning: false },
};

const ModalMembershipConversion = ({ group, share, onClose, onSelect }: Props) => {
    const [selected, setSelected] = useState<MembershipConversionChoice | null>(null);
    const [hovered, setHovered] = useState<MembershipConversionChoice | null>(null);
    const stateByKey: Record<MembershipConversionChoice, MembershipConversionOptionState> = {
        group,
        share,
    };
    const selectedState = selected ? stateByKey[selected] : null;

    return (
        <Modal whiteViewWidth={760} isCloseButton isWhiteOverFlowScroll={false} onClose={onClose}>
            <div style={{ padding: '36px 40px 32px' }}>
                <h2 className="text-[22px] font-bold text-[#111827]">수강권 전환</h2>
                <p className="mt-[8px] text-[14px] leading-[22px] text-[#6B7280]">
                    바꿀 방식을 고르세요. 둘 다 이미 완료된 수업과 결제 기록은 그대로 둡니다.
                </p>

                <div className="mt-[24px] grid grid-cols-2 gap-[14px]">
                    {OPTIONS.map((option) => {
                        const state = stateByKey[option.key];
                        const isSelected = selected === option.key;
                        const isHovered = hovered === option.key && state.isAvailable;
                        const reversibility = REVERSIBILITY[option.key];

                        return (
                            <button
                                key={option.key}
                                type="button"
                                role="radio"
                                aria-checked={isSelected}
                                disabled={!state.isAvailable}
                                onMouseEnter={() => setHovered(option.key)}
                                onMouseLeave={() => setHovered(null)}
                                onClick={() => setSelected(option.key)}
                                className="relative flex flex-col rounded-[12px] p-[20px] text-left transition-all disabled:cursor-not-allowed"
                                style={{
                                    // 테두리 색만 바꾸면 색각 이상에서 선택 상태가 구분되지 않는다.
                                    // 굵기·그림자·체크 배지를 함께 바꾼다.
                                    border: `1px solid ${
                                        !state.isAvailable
                                            ? '#E3E8F0'
                                            : isSelected
                                            ? option.accent
                                            : isHovered
                                            ? '#B6C4DC'
                                            : '#DCE3ED'
                                    }`,
                                    boxShadow: isSelected
                                        ? `inset 0 0 0 1px ${option.accent}, 0 8px 20px rgba(16,24,40,0.10)`
                                        : isHovered
                                        ? '0 6px 16px rgba(16,24,40,0.08)'
                                        : 'none',
                                    transform: isHovered && !isSelected ? 'translateY(-1px)' : 'translateY(0)',
                                    backgroundColor: !state.isAvailable ? '#F7F9FC' : '#FFFFFF',
                                    opacity: state.isAvailable ? 1 : 0.7,
                                }}
                            >
                                {isSelected && (
                                    <span
                                        aria-hidden
                                        className="absolute right-[16px] top-[16px] flex h-[22px] w-[22px] items-center justify-center rounded-full text-white"
                                        style={{ backgroundColor: option.accent }}
                                    >
                                        <Check size={14} strokeWidth={3} />
                                    </span>
                                )}

                                <span
                                    aria-hidden
                                    className="flex h-[40px] w-[40px] items-center justify-center rounded-[10px]"
                                    style={{
                                        backgroundColor: state.isAvailable ? option.accentSoft : '#EEF1F5',
                                        color: state.isAvailable ? option.accent : '#98A2B3',
                                    }}
                                >
                                    {state.isAvailable ? option.icon : <Lock size={18} />}
                                </span>

                                <span
                                    className="mt-[14px] text-[13px] font-semibold"
                                    style={{ color: state.isAvailable ? option.accent : '#98A2B3' }}
                                >
                                    {option.title}
                                </span>
                                {/* 카드에서 가장 크게 읽혀야 할 줄. 이 한 문장으로 고를 수 있어야 한다. */}
                                <span
                                    className="mt-[4px] text-[17px] font-bold leading-[25px]"
                                    style={{ color: state.isAvailable ? '#111827' : '#7B8493' }}
                                >
                                    {option.headline}
                                </span>

                                <dl className="mt-[16px] border-t border-[#EEF1F5] pt-[12px]">
                                    {option.facts.map((fact) => (
                                        <div key={fact.label} className="flex gap-[10px] py-[4px]">
                                            <dt className="w-[68px] shrink-0 text-[12px] text-[#98A2B3]">
                                                {fact.label}
                                            </dt>
                                            <dd className="m-0 text-[12px] leading-[18px] text-[#505967]">
                                                {fact.value}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>

                                {/* 되돌릴 수 있는지가 이 선택의 무게를 정한다. 표 안에 묻지 않고 따로 세운다. */}
                                <span
                                    className="mt-[12px] inline-flex w-fit items-center gap-[6px] rounded-[6px] px-[8px] py-[5px] text-[12px] font-semibold"
                                    style={{
                                        backgroundColor: reversibility.isWarning ? '#FFF1F2' : '#F1F8F6',
                                        color: reversibility.isWarning ? '#B4232D' : '#0A7563',
                                    }}
                                >
                                    {reversibility.isWarning ? <Lock size={13} /> : <Check size={13} />}
                                    {reversibility.text}
                                </span>

                                {!state.isAvailable && state.unavailableReason && (
                                    <span className="mt-[12px] rounded-[8px] bg-[#FFF8E6] px-[10px] py-[8px] text-[12px] leading-[18px] text-[#8A5A00]">
                                        {state.unavailableReason}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-[24px] flex items-center justify-between gap-[16px]">
                    <p className="text-[12px] text-[#98A2B3]">
                        {selectedState?.isAvailable
                            ? '다음 단계에서 세부 내용을 확인하고 확정합니다.'
                            : '카드를 눌러 방식을 먼저 고르세요.'}
                    </p>
                    <div className="flex shrink-0 items-center gap-[10px]">
                        <button
                            type="button"
                            className="h-[42px] rounded-[8px] border border-[#DCE3ED] bg-white px-[18px] text-[14px] font-semibold text-[#5B6472] hover:bg-[#F4F7FC]"
                            onClick={onClose}
                        >
                            취소
                        </button>
                        <button
                            type="button"
                            disabled={!selectedState?.isAvailable}
                            className="flex h-[42px] items-center gap-[6px] rounded-[8px] px-[20px] text-[14px] font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed"
                            style={{
                                backgroundColor: selectedState?.isAvailable
                                    ? OPTIONS.find((option) => option.key === selected)?.accent
                                    : '#C4CBD6',
                            }}
                            onClick={() => {
                                if (!selected || !selectedState?.isAvailable) return;
                                onSelect(selected);
                            }}
                        >
                            다음
                            <ArrowRight size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ModalMembershipConversion;
